import { FC, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { FaStar } from "react-icons/fa";
import { AiOutlineFilePdf } from "react-icons/ai";
import { Layout } from "../../components/Layout";
import PageHeader from "../../components/PageHeader/PageHeader";
import { PageContent } from "../../components";
import { useSearch } from "../../context/SearchContext";
import { FirestoreContext } from "../../context/Context";
import { timestampConverter } from "../Article/Articles";
import { themesPdfs } from "../Theme/Themes";
import { Article, Lyrical, Gold } from "../../interfaces";

const SearchSection = styled.div`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  margin-bottom: 20px;
  border-bottom: 2px solid #f0e6d2;
  padding-bottom: 10px;
  color: #333;
`;

const ResultItem = styled.div`
  padding: 15px 0;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
`;

const ResultTitle = styled.h3`
  font-size: 18px;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ResultDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 5px;
`;

const ResultType = styled.span`
  font-size: 12px;
  text-transform: uppercase;
  color: #999;
  letter-spacing: 1px;
`;

const SearchResults: FC = () => {
  const { searchQuery } = useSearch();
  const data = useContext(FirestoreContext);
  const navigate = useNavigate();

  const [results, setResults] = useState<{
    articles: Article[];
    lyricals: Lyrical[];
    gold: any[];
  }>({ articles: [], lyricals: [], gold: [] });

  useEffect(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) {
      setResults({ articles: [], lyricals: [], gold: [] });
      return;
    }

    const filteredArticles = data.articles.filter(
      (a) =>
        a.title.toLowerCase().includes(query) ||
        a.description.toLowerCase().includes(query) ||
        a.content.toLowerCase().includes(query)
    );

    const filteredLyricals = data.lyricals.filter(
      (l) =>
        l.title.toLowerCase().includes(query) ||
        l.content.toLowerCase().includes(query) ||
        l.type.toLowerCase().includes(query)
    );

    const filteredGold = themesPdfs.filter((g) =>
      g.title.toLowerCase().includes(query)
    );

    setResults({
      articles: filteredArticles,
      lyricals: filteredLyricals,
      gold: filteredGold,
    });
  }, [searchQuery, data]);

  const { setSearchQuery } = useSearch();


  const goToArticle = (id: string) => {
    setSearchQuery("");
    navigate(`/nuggets/${id}`);
  };
  const goToLyrical = (id: string) => {
    setSearchQuery("");
    navigate(`/gems/${id}`);
  };
  const goToGold = (item: Gold) => {
    window.open(item.data, "_blank");
  };

  const hasResults =
    results.articles.length > 0 ||
    results.lyricals.length > 0 ||
    results.gold.length > 0;

  return (
    <Layout>
      <PageHeader
        title="Search Results"
        description={
          hasResults
            ? `Showing results for "${searchQuery}"`
            : `No results found for "${searchQuery}"`
        }
      />
      <PageContent mb={100}>
        {results.articles.length > 0 && (
          <SearchSection>
            <SectionTitle>Nuggets</SectionTitle>
            {results.articles.map((article) => (
              <ResultItem key={article.id} onClick={() => goToArticle(article.id)}>
                <ResultTitle>
                  {article.isFavorite && <FaStar color="gold" size={18} />}
                    {article.title}
                  </ResultTitle>
                  <ResultDescription>{article.description}</ResultDescription>
                  <ResultType>Article • {timestampConverter(article.date_published)}</ResultType>
              </ResultItem>
            ))}
          </SearchSection>
        )}
        {results.lyricals.length > 0 && (
          <SearchSection>
            <SectionTitle>Gems</SectionTitle>
            {results.lyricals.map((lyrical) => (
              <ResultItem key={lyrical.id} onClick={() => goToLyrical(lyrical.id)}>
                <ResultTitle>{lyrical.title}</ResultTitle>
                <ResultType>{lyrical.type}</ResultType>
              </ResultItem>
            ))}
          </SearchSection>
        )}
        {results.gold.length > 0 && (
          <SearchSection>
            <SectionTitle>Gold</SectionTitle>
            {results.gold.map((item) => (
              <ResultItem key={item.id} onClick={() => goToGold(item)}>
                <ResultTitle>
                  <AiOutlineFilePdf size={24} style={{ marginRight: "10px" }} />
                    {item?.title}
                  </ResultTitle>
              </ResultItem>
            ))}
          </SearchSection>
        )}
      </PageContent>
    </Layout>
  );
};

export default SearchResults;
